import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { applicationStore } from "src/store/ApplicationStore";
import ProcessLoader from "src/components/animation/ProcessLoader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { autorun } from "mobx";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import logger from "src/utils/logger";

const ProcessingReview = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showTooltip, setShowTooltip] = useState(false);
    const returnUrl = sessionStorage.getItem("returnUrl") === "null" ? null : sessionStorage.getItem("returnUrl");

    const handleCopyLink = () => {
        navigator.clipboard.writeText(
            `${window?.location?.origin}?aid=${applicationStore.aidCode}`
        );
        setShowTooltip(true);
        setTimeout(() => {
            setShowTooltip(false);
        }, 1000);
    };

    // On every mount (including page refresh) re-fetch the latest workflow state.
    // A socket message can arrive while the page is reloading and get lost; the bare
    // /workflow/next request returns the current backend state so the autorun below
    // can still route the user forward.
    useEffect(() => {
        const refreshWorkflowState = async () => {
            try {
                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                });
                applicationStore.setWorkflowNextServiceResponse(response.data);

                if (response.data?.waiting_for_pep_step === true) {
                    applicationStore.setPepStatus('success');
                }

                const inquiry_status = response.data?.inquiry_status;
                if (inquiry_status) {
                    applicationStore.setInquiryStatus(inquiry_status);
                }
            } catch (error) {
                logger.error("ProcessingReview: failed to refresh workflow state on mount", error);
            }
        };

        refreshWorkflowState();
    }, []);

    useEffect(() => {
        const disposer = autorun(() => {
            const pepStatus = applicationStore.pepStatus;
            const inquiry_status = applicationStore.inquiry_status;

            if (pepStatus === 'failed') {
                applicationStore.setApplicationErrorObj({
                    header: t('processing.reviewFailedHeader'),
                    description: t('processing.reviewFailedDescription'),
                    footerDescription: t('processing.reviewFailedFooter'),
                    listItems: [],
                    buttonList: returnUrl ? [{
                        label: t('success.goHome'),
                        href: returnUrl
                    }] : []
                });
                navigate("/error-page", { replace: true });
                return;
            }
            if (inquiry_status === 'approved') {
                setTimeout(() => {
                    navigate("/success", { replace: true });
                }, 500);
                return;
            }
        });
        return () => disposer();
    }, [navigate, t, returnUrl]);

    return (
        <div className="custom-container">
            <div className="flex flex-col items-center h-full pt-[10px]">
                <div className="pt-8">
                    <div className="text-h5 font-bold mb-2 text-flow-heading">{t('processing.title')}</div>
                    <div className="text-body text-Gray900">{t('processing.subtitle')}</div>
                    <ProcessLoader />
                </div>
                <div className="text-left z-10">
                    <ul className="list-disc list-inside text-Gray900 mt-3">
                        <li>{t('processing.noAction')}</li>
                        <li>
                            {t('processing.checkBack')}
                            {window?.top === window?.self && <span
                                onClick={handleCopyLink}
                                className="relative flow-inline-accent-link ms-1"
                            >
                                {t('processing.clickToCopy')}
                                {showTooltip && (
                                    <div
                                        className={`absolute z-30 top-5 left-24 bg-SorrellBrown4000 text-xxs text-sm py-1 px-2 rounded-[16px] border-SorrellBrown4000 text-Gray50 shadow-lg ${showTooltip ? "animate-fadeIn" : "animate-fadeOut"
                                            }`}
                                    >
                                        {t('processing.copied')}
                                    </div>
                                )}
                            </span>}
                        </li>
                        <li>{t('processing.takesLonger')}</li>
                    </ul>
                </div>
            </div>
        </div>
    );
});

export default ProcessingReview;
